import React, {useRef, useState} from 'react';
import {classes, style, vars} from './scrollbar.st.css';
import {RenderFunction, useElementSizeBinding, useViewportBinding} from '../index';
import {
    Evaluable,
    evaluateWhenFunction,
    HorizontalDirections, numberNormalizeToRange,
    Orientation,
    VerticalDirections
} from '@wix/devzai-utils-common';
import {PointerInteraction, pointerLikeEventGetOffset} from '@wix/devzai-utils-dom';
import { reactCreateMemo } from "../react-utils/react-utils";

export interface ScrollbarThumbRenderProps {
    isActive: boolean;
}

export interface Scrollbar_Props {
    orientation: Orientation;
    scrollViewRef: React.RefObject<HTMLElement>;
    minThumbSize?: number;
    maxThumbSize?: number;
    thumb?: Evaluable<RenderFunction<ScrollbarThumbRenderProps, React.ReactElement>>
    autoHide?: boolean;
    interactiveTrack?: boolean;
}

export const Scrollbar = reactCreateMemo(function Scrollbar(props: Scrollbar_Props & React.HTMLAttributes<any>) {
    const {
        scrollViewRef,
        orientation,
        minThumbSize = 20,
        maxThumbSize = Infinity,
        autoHide = true,
        interactiveTrack = true,
        thumb,

        className,
        style: styleAttributes,
        ...htmlAttributes
    } = props;

    const trackElementRef = useRef<HTMLDivElement>(null);
    const thumbElementRef = useRef<HTMLDivElement>(null);

    const { scrollLeft, scrollTop, scrollWidth, scrollHeight, width, height } = useViewportBinding(scrollViewRef);

    const trackElementSize = useElementSizeBinding(trackElementRef);
    const [isThumbActive, setIsThumbActive] = useState(false);

    const scrollSize = orientation === Orientation.Vertical ? scrollHeight : scrollWidth;
    const viewportSize = orientation === Orientation.Vertical ? height : width;
    const trackLength = trackElementSize
        ? orientation === Orientation.Vertical
            ? trackElementSize.height
            : trackElementSize.width
        : undefined;

    const scrollValue = orientation === Orientation.Vertical ? scrollTop : scrollLeft;

    const maxScrollValue = Math.max(0, scrollSize - viewportSize);
    const canScroll = maxScrollValue > 0;

    const thumbLength =
        trackLength !== undefined && canScroll ?
            numberNormalizeToRange(trackLength - maxScrollValue, minThumbSize, maxThumbSize) : 0;

    const step = trackLength !== undefined && canScroll ? maxScrollValue / (trackLength - thumbLength) : 1;

    const thumbOffset = scrollValue / step;

    const orientationCssState = orientation === Orientation.Vertical ? 'vertical' : 'horizontal';

    const htmlScrollProp = orientation === Orientation.Vertical ? 'scrollTop' : 'scrollLeft';

    const onTrackInteraction = interactiveTrack
        ? (event: React.MouseEvent | React.TouchEvent) => {
              const eventOffset = pointerLikeEventGetOffset(event.nativeEvent);
              const thumbElementArea = thumbElementRef.current!.getBoundingClientRect();

              const isBeforeThumb =
                  orientation === Orientation.Vertical
                      ? eventOffset.top < thumbElementArea.top
                      : eventOffset.left < thumbElementArea.left;

              const factor = isBeforeThumb ? -1 : 1;

              scrollViewRef.current![htmlScrollProp] = Math.min(
                  scrollValue + (trackLength ? trackLength : 0) * factor,
                  maxScrollValue
              );
          }
        : undefined;

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, props.className)}
            style={{
                ...styleAttributes
            }}
            hidden={autoHide && !canScroll}
        >
            <div
                ref={trackElementRef}
                className={style(classes.track, {})}
                style={{
                    [vars.thumbOffset]: thumbOffset + 'px',
                    [vars.thumbLength]: thumbLength + 'px'
                }}
                onMouseDown={onTrackInteraction}
            >
                <div
                    ref={thumbElementRef}
                    className={style(classes.thumb, {
                        orientation: orientationCssState
                    })}
                    onMouseDown={event => {
                        event.stopPropagation();
                        event.preventDefault();

                        PointerInteraction.start(event.nativeEvent, {
                            allowedDirections:
                                orientation === Orientation.Vertical ? VerticalDirections : HorizontalDirections,
                            startCallback: interaction => {
                                setIsThumbActive(true);

                                const interactionOverlay = createInteractionOverlay();
                                document.body.appendChild(interactionOverlay);

                                const initialScrollValue = scrollValue;

                                return {
                                    moveCallback: () => {
                                        const delta =
                                            orientation === Orientation.Vertical
                                                ? interaction.getTotalDeltaY()
                                                : interaction.getTotalDeltaX();

                                        scrollViewRef.current![htmlScrollProp] = Math.min(
                                            initialScrollValue + step * delta,
                                            maxScrollValue
                                        );
                                    },
                                    endCallback: () => {
                                        interactionOverlay.parentElement!.removeChild(interactionOverlay);

                                        setIsThumbActive(false);
                                    }
                                };
                            }
                        });
                    }}
                >
                    {evaluateWhenFunction(thumb, { isActive: isThumbActive })}
                </div>
            </div>
        </div>
    );
});

function createInteractionOverlay() {
    const interactionOverlay = document.createElement('div');

    Object.assign(interactionOverlay.style, {
        position: 'fixed',
        left: '0',
        top: '0',
        bottom: '0',
        right: '0',
        zIndex: '999999'
    });

    return interactionOverlay;
}
